import React, { useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useIntl } from 'react-intl';
import {
  TextField,
  RadioGroup,
  Radio,
  FormControlLabel,
  Box,
  IconButton,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';

import ButtonWithLoader from '../../../../../components/other/Buttons/ButtonWithLoader';

import { IEnumValue, ITypeParameter } from '../../../../../interfaces/productType';
import homeStyles from '../../../../../constants/homeStyles';
import { useStylesTypeForm } from '../../hooks/useStyles';

interface IProps {
  param: ITypeParameter;
  paramAction: any;
  deleteField: (id?: number, index?: number) => void;
  index: number;
  disabled: boolean;
  fetchDeleteEnumValue: (parameter_id: number) => void;
}

const getInitialValues = (param: ITypeParameter) => {
  const params: any = {
    name: param.name,
    enum_data: param.parameter_enum_values || [],
    type: param.type || 'number',
    id: param.id,
  };
  return params;
};
const TypeParamForm: React.FC<IProps> = ({
  param,
  paramAction,
  deleteField,
  index,
  disabled,
  fetchDeleteEnumValue,
}) => {
  const intl = useIntl();
  const homeClasses = homeStyles();
  const classes1 = useStylesTypeForm();
  const { classes } = classes1;
  const [enumParams, setEnumParams] = useState<
    {
      value: string;
      favorite: boolean;
      image?: File;
    }[]
  >([{ value: '', favorite: false }]);
  const addEmptyEnumParam = () => {
    setEnumParams([...enumParams, { value: '', favorite: false }]);
  };

  const {
    values,
    setFieldValue,
    handleSubmit,
    handleBlur,
    touched,
    errors,
    handleChange,
    isSubmitting,
  } = useFormik({
    initialValues: getInitialValues(param),
    enableReinitialize: true,
    validationSchema: Yup.object().shape({
      name: Yup.string()
        .required(intl.formatMessage({ id: 'AUTH.VALIDATION.REQUIRED_FIELD' }))
        .nullable(),
    }),
    onSubmit: (values, { setSubmitting }) => {
      if (values.type === 'enum' || values.type === 'multiple_enum') {
        setEnumParams([{ value: '', favorite: false }]);
        paramAction(
          {
            type: values.type,
            name: values.name,
            id: values.id,
          },
          values.enum_data,
          enumParams
        );
      } else {
        paramAction({
          ...values,
        });
      }
      setSubmitting(false);
    },
  });

  return (
    <Box
      className={homeClasses.classes.paramContainer}
      border={1}
      borderColor='#eeeeee'
      borderRadius={5}
    >
      <div>
        <div className={classes.containerName}>
          <TextField
            type='text'
            label={intl.formatMessage({
              id: 'PRODUCT.TYPES.PARAMETER.INPUT.NAME',
            })}
            margin='normal'
            name='name'
            value={values.name || ''}
            variant='outlined'
            onBlur={handleBlur}
            onChange={e => {
              setFieldValue('name', e.target.value);
            }}
            helperText={touched.name && errors.name?.toString()}
            error={Boolean(touched.name && errors.name)}
          />
          {param.id && (
            <IconButton
              size='small'
              onClick={() => deleteField(param.id, index)}
              className={classes.leftIcon}
            >
              <DeleteIcon color='error' />
            </IconButton>
          )}
        </div>
        <RadioGroup name='type' value={values.type} onChange={handleChange}>
          <FormControlLabel
            value='number'
            control={<Radio />}
            label={intl.formatMessage({
              id: 'PRODUCT.TYPES.PARAMETER.NUMBER',
            })}
            disabled={!!param.id}
          />
          <FormControlLabel
            value='enum'
            control={<Radio />}
            label={intl.formatMessage({
              id: 'PRODUCT.TYPES.PARAMETER.LIST',
            })}
            disabled={!!param.id}
          />
          <FormControlLabel
            value='text'
            control={<Radio />}
            label={intl.formatMessage({
              id: 'PRODUCT.TYPES.PARAMETER.TEXT',
            })}
            disabled={!!param.id}
          />
          <FormControlLabel
            value='string'
            control={<Radio />}
            label={intl.formatMessage({
              id: 'PRODUCT.TYPES.PARAMETER.STRING',
            })}
            disabled={!!param.id}
          />
          <FormControlLabel
            value='multiple_enum'
            control={<Radio />}
            label={intl.formatMessage({
              id: 'PRODUCT.TYPES.MULTIPLY.ENUM',
            })}
            disabled={!!param.id}
          />
          <FormControlLabel
            value='country'
            control={<Radio />}
            label={intl.formatMessage({ id: 'PRODUCT.TYPES.LIST.COUNTRIES' })}
            disabled={!!param.id}
          />
        </RadioGroup>
        {(values.type === 'enum' || values.type === 'multiple_enum') && values.enum_data && (
          <div>
            {values.enum_data.map((enumParam: IEnumValue, index: number) => (
              <>
                <div className={homeClasses.classes.textFieldContainer} key={`i${index}`}>
                  <TextField
                    style={{ marginRight: 5 }}
                    className={classes.typeName}
                    type='text'
                    label={`${intl.formatMessage({
                      id: 'PRODUCT.TYPES.PARAMETER.INPUT.VALUE',
                    })}`}
                    margin='normal'
                    value={enumParam.value}
                    variant='outlined'
                    onBlur={handleBlur}
                    onChange={e => {
                      setFieldValue(`enum_data[${index}].value`, e.target.value);
                    }}
                  />
                  <IconButton
                    size='small'
                    onClick={() => {
                      fetchDeleteEnumValue(enumParam.id);
                    }}
                    className={homeClasses.classes.leftIcon}
                  >
                    <DeleteIcon color='error' />
                  </IconButton>
                </div>
              </>
            ))}
            {enumParams.map((enumParam, index) => (
              <>
                <div className={homeClasses.classes.textFieldContainer} key={`i${index}`}>
                  <TextField
                    style={{ marginRight: 5 }}
                    className={classes.typeName}
                    type='text'
                    label={`${intl.formatMessage({
                      id: 'PRODUCT.TYPES.PARAMETER.INPUT.VALUE',
                    })}`}
                    margin='normal'
                    value={enumParam.value}
                    variant='outlined'
                    onBlur={handleBlur}
                    onChange={e => {
                      setEnumParams(state => {
                        const copy = [...state];
                        copy[index] = {
                          ...copy[index],
                          value: e.target.value,
                        };
                        return copy;
                      });
                    }}
                  />
                  <IconButton
                    size='small'
                    onClick={() => {
                      setEnumParams(state => {
                        const copy = [...state];
                        copy.splice(index, 1);
                        return copy;
                      });
                    }}
                    className={homeClasses.classes.leftIcon}
                  >
                    <DeleteIcon color='error' />
                  </IconButton>
                </div>
              </>
            ))}{' '}
            <IconButton
              size='small'
              onClick={addEmptyEnumParam}
              className={homeClasses.classes.leftIcon}
            >
              <AddIcon />
            </IconButton>
          </div>
        )}
        <div className={homeClasses.classes.buttonContainer}>
          <ButtonWithLoader
            loading={isSubmitting || disabled}
            onPress={handleSubmit}
            disabled={disabled}
          >
            {intl.formatMessage({ id: 'PRODUCT.TYPES.PARAMETER.BUTTON.SAVE' })}
          </ButtonWithLoader>
        </div>
      </div>
    </Box>
  );
};

export default React.memo(TypeParamForm);
